import { Input } from "@components/ui/input";
import { PlusIcon, SearchIcon, XIcon } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import regexList from "@utils/regexList";
import { RecruiterTypes } from "@utils/constants";
import {
  getRecruiterInvitations,
  approvalRequest,
  inviteRecruiterMember,
  updateRecruiterInvitationSso,
} from "@endpoints/recruiter.service";
import dayjs from "dayjs";
import { RecruiterApprovalStatus } from "@utils/constants";
import { useSelector } from "react-redux";

type RecruiterInvitation = {
  _id: string;
  agencyId?: string;
  firstName: string;
  lastName: string;
  company: string;
  jobTitle?: string;
  phone?: string;
  email: string;
  address1: string;
  address2?: string;
  city?: string;
  country: string;
  postCode?: string;
  role: number;
  status: "Pending" | "Accepted";
  ssoEnabled?: boolean;
  refNo?: string;
  userRegisterAt?: string;
  lastActive?: string;
  recruiterApprovalStatus?: number;
};

type InviteFormValues = {
  firstName: string;
  lastName: string;
  company: string;
  jobTitle?: string;
  phone?: string;
  email: string;
  address1: string;
  address2?: string;
  city?: string;
  country: string;
  postCode?: string;
  role: number;
  ssoEnabled?: boolean;
};

const roleOptions = [
  { label: "Admin", value: RecruiterTypes.ADMIN },
  { label: "Manager", value: RecruiterTypes.SUBADMIN },
  { label: "Staff member", value: RecruiterTypes.MEMBER },
];

const AccountMembers = () => {
  const { enqueueSnackbar } = useSnackbar();
  const companyName = useSelector((state: any) => {
    return (
      state?.auth?.companyDetails?.name ||
      state?.auth?.user?.companyName ||
      ""
    );
  });
  const [invitations, setInvitations] = useState<RecruiterInvitation[]>([]);
  const [isInvitesLoading, setIsInvitesLoading] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [ssoUpdatingId, setSsoUpdatingId] = useState<string | null>(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [selectedApprovalInvite, setSelectedApprovalInvite] =
    useState<RecruiterInvitation | null>(null);
  const [approvalActionLoading, setApprovalActionLoading] = useState<
    "approve" | "reject" | null
  >(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
    watch,
  } = useForm<InviteFormValues>({
    defaultValues: {
      firstName: "",
      lastName: "",
      company: "",
      jobTitle: "",
      phone: "",
      email: "",
      address1: "",
      address2: "",
      city: "",
      country: "",
      postCode: "",
      role: RecruiterTypes.ADMIN,
      ssoEnabled: false,
    },
  });

  const fields = watch();

  useEffect(() => {
    if (companyName) {
      setValue("company", companyName);
    }
  }, [companyName, setValue]);

  const filteredInvitations = useMemo(() => {
    if (!searchText?.trim()) return invitations;
    const term = searchText.toLowerCase();
    return invitations.filter((invite) => {
      const name = `${invite.firstName} ${invite.lastName}`.toLowerCase();
      return (
        name.includes(term) ||
        invite.email.toLowerCase().includes(term) ||
        (invite.refNo || "").toLowerCase().includes(term)
      );
    });
  }, [invitations, searchText]);

  async function fetchRecruiterInvitations() {
    try {
      setIsInvitesLoading(true);
      const { success, data } = await getRecruiterInvitations();
      if (success) {
        setInvitations(data || []);
      }
    } catch (error) {
      enqueueSnackbar(error || "Failed to fetch invitations", {
        variant: "error",
      });
    } finally {
      setIsInvitesLoading(false);
    }
  }

  const handleInvite = async (payload: InviteFormValues) => {
    try {
      setIsInviting(true);
      const { success } = await inviteRecruiterMember({
        ...payload,
        company: companyName || payload.company,
        role: Number(payload.role),
        ssoEnabled: Boolean(payload.ssoEnabled),
      });
      if (success) {
        enqueueSnackbar("Invitation sent successfully.", {
          variant: "success",
        });
        reset({
          firstName: "",
          lastName: "",
          company: "",
          jobTitle: "",
          phone: "",
          email: "",
          address1: "",
          address2: "",
          city: "",
          country: "",
          postCode: "",
          role: RecruiterTypes.ADMIN,
          ssoEnabled: false,
        });
        setIsInviteModalOpen(false);
        await fetchRecruiterInvitations();
      }
    } catch (error) {
      enqueueSnackbar(error || "Failed to send invitation", {
        variant: "error",
      });
    } finally {
      setIsInviting(false);
    }
  };

  const handleToggleSso = async (inviteId: string, nextValue: boolean) => {
    try {
      setSsoUpdatingId(inviteId);
      const { success, data } = await updateRecruiterInvitationSso({
        inviteId,
        ssoEnabled: nextValue,
      });
      if (success) {
        setInvitations((prev) =>
          prev.map((item) => (item._id === inviteId ? data : item)),
        );
      }
    } catch (error) {
      enqueueSnackbar(error || "Failed to update SSO", { variant: "error" });
    } finally {
      setSsoUpdatingId(null);
    }
  };

  const handleOpenApprovalModal = (invite: RecruiterInvitation) => {
    setSelectedApprovalInvite(invite);
  };

  const handleCloseApprovalModal = () => {
    setSelectedApprovalInvite(null);
    setApprovalActionLoading(null);
  };

  const handleApprovalAction = async (status: 1 | 2) => {
    const recruiterId =
      selectedApprovalInvite?.agencyId || selectedApprovalInvite?._id;
    if (!recruiterId) return;
    try {
      setApprovalActionLoading(status === 1 ? "approve" : "reject");
      const data = await approvalRequest({
        recruiterId,
        status,
      });

      if (data.success) {
        enqueueSnackbar(data.data.message, {
          variant: "success",
        });
        handleCloseApprovalModal();
        await fetchRecruiterInvitations();
      }
    } catch (error) {
      enqueueSnackbar(error || "Failed to update approval request", {
        variant: "error",
      });
    } finally {
      setApprovalActionLoading(null);
    }
  };

  useEffect(() => {
    fetchRecruiterInvitations();
  }, []);

  return (
    <React.Fragment>
      <div className="flex flex-col flex-1 gap-6">
        <div className="bg-white p-6 rounded-md border border-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-sm font-semibold">Invited Members</p>
            <div className="flex flex-wrap items-center gap-3">
              {invitations.length > 0 ? (
                <div className="flex items-center bg-white pr-3 border rounded-md w-72">
                  <Input
                    className="!focus-visible:ring-[3px] border-none !text-xs font-medium"
                    placeholder="Search by name, email, reference..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  <SearchIcon className="w-4 h-4" color="#2e2e2e" />
                </div>
              ) : null}
              <button
                type="button"
                className="flex items-center justify-center rounded-md py-2 px-3 gap-2 bg-secondary hover:bg-orange-400 text-white font-semibold text-xs"
                onClick={() => setIsInviteModalOpen(true)}
              >
                <PlusIcon className="w-4 h-4" color="white" />
                Invite Member
              </button>
            </div>
          </div>

          {isInvitesLoading ? (
            <div className="flex items-center justify-center py-10">
              <LoadingAnimation className="h-6 w-6" color="#2e2e2e" />
            </div>
          ) : filteredInvitations.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10">
              <span className="font-medium text-sm text-light-black">
                No invited members available.
              </span>
            </div>
          ) : (
            <div className="flex flex-col">
              <div className="grid grid-cols-[1.2fr_1fr_0.8fr_0.6fr_0.6fr] border-b pb-3">
                <span className="font-bold text-xs text-light-black">
                  Member
                </span>
                <span className="font-bold text-xs text-light-black">Role</span>
                <span className="font-bold text-xs text-light-black">
                  RegisterAt
                </span>
                <span className="font-bold text-xs text-light-black text-center">
                  Approval
                </span>
                <span className="font-bold text-xs text-light-black text-center">
                  SSO
                </span>
              </div>
              <div className="flex flex-col">
                {filteredInvitations.map((invite) => {
                  const roleLabel =
                    roleOptions.find((opt) => opt.value === invite.role)
                      ?.label || "-";
                  const approvalStatus = invite.recruiterApprovalStatus;
                  const recruiterId = invite.agencyId || invite._id;
                  const approvalLabel =
                    approvalStatus === RecruiterApprovalStatus.ACCEPTED
                      ? "Approved"
                      : approvalStatus === RecruiterApprovalStatus.REJECTED
                      ? "Rejected"
                      : approvalStatus === RecruiterApprovalStatus.REQUESTED
                      ? "Pending approval"
                      : invite.status === "Accepted"
                      ? "Pending approval"
                      : "Invitation pending";
                  const approvalClass =
                    approvalStatus === RecruiterApprovalStatus.ACCEPTED
                      ? "text-[#1e8528] bg-[#dcf7d6]"
                      : approvalStatus === RecruiterApprovalStatus.REJECTED
                      ? "text-[#d72417] bg-[#fbd9ce]"
                      : "text-[#A43370] bg-[#F7DEEC]";
                  return (
                    <div
                      key={invite._id}
                      className="grid grid-cols-[1.2fr_1fr_0.8fr_0.6fr_0.6fr] py-4 border-b"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium text-xs text-light-black">
                          {invite.firstName} {invite.lastName}
                        </span>
                        <span className="text-[11px] text-gray-500">
                          {invite.email}
                        </span>
                      </div>
                      <span className="font-medium text-xs text-light-black flex items-center capitalize">
                        {roleLabel}
                      </span>
                      <span className="font-medium text-xs text-light-black flex items-center">
                        {invite?.userRegisterAt
                          ? dayjs(invite.userRegisterAt).format("YYYY-MM-DD")
                          : "-"}
                      </span>
                      <span className="font-medium text-xs text-light-black flex items-center justify-center">
                        {approvalStatus === RecruiterApprovalStatus.REQUESTED ? (
                          <button
                            type="button"
                            className={`rounded px-3 py-1 text-xs ${approvalClass} cursor-pointer`}
                            onClick={() =>
                              recruiterId ? handleOpenApprovalModal(invite) : null
                            }
                          >
                            {approvalLabel}
                          </button>
                        ) : (
                          <span className={`rounded px-3 py-1 text-xs ${approvalClass}`}>
                            {approvalLabel}
                          </span>
                        )}
                      </span>
                      <div className="flex items-center justify-center">
                        <input
                          type="checkbox"
                          checked={!!invite.ssoEnabled}
                          disabled={ssoUpdatingId === invite._id}
                          onChange={(e) =>
                            handleToggleSso(invite._id, e.target.checked)
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {isInviteModalOpen ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white w-full max-w-4xl rounded-lg border border-gray-200 shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <p className="text-sm font-semibold">Invite Member</p>
              <button
                type="button"
                className="p-1 text-gray-500 hover:text-gray-700"
                onClick={() => setIsInviteModalOpen(false)}
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit(handleInvite)}>
              <div className="px-6 py-5">
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex flex-col gap-4">
                    <div>
                      <label className="text-xs font-medium">
                        First name *
                      </label>
                      <Input
                        {...register("firstName", {
                          required: "First name is required",
                        })}
                        className="mt-1 text-xs"
                      />
                      {errors.firstName && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.firstName.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-medium">Last name *</label>
                      <Input
                        {...register("lastName", {
                          required: "Last name is required",
                        })}
                        className="mt-1 text-xs"
                      />
                      {errors.lastName && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.lastName.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-medium">Company *</label>
                      <Input
                        {...register("company", {
                          required: "Company is required",
                        })}
                        className="mt-1 text-xs"
                        value={fields.company || companyName}
                        readOnly={Boolean(companyName)}
                        disabled={Boolean(companyName)}
                      />
                      {errors.company && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.company.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-medium">Job Title</label>
                      <Input
                        {...register("jobTitle")}
                        className="mt-1 text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium">Phone</label>
                      <Input {...register("phone")} className="mt-1 text-xs" />
                    </div>
                    <div>
                      <label className="text-xs font-medium">
                        Email Address *
                      </label>
                      <Input
                        {...register("email", {
                          required: "Email is required",
                          pattern: {
                            value: regexList.email,
                            message: "Invalid email address",
                          },
                        })}
                        className="mt-1 text-xs"
                      />
                      {errors.email && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.email.message}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-4">
                    <div>
                      <label className="text-xs font-medium">Address 1 *</label>
                      <Input
                        {...register("address1", {
                          required: "Address 1 is required",
                        })}
                        className="mt-1 text-xs"
                      />
                      {errors.address1 && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.address1.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-medium">Address 2</label>
                      <Input
                        {...register("address2")}
                        className="mt-1 text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium">City</label>
                      <Input {...register("city")} className="mt-1 text-xs" />
                    </div>
                    <div>
                      <label className="text-xs font-medium">Country *</label>
                      <Input
                        {...register("country", {
                          required: "Country is required",
                        })}
                        className="mt-1 text-xs"
                      />
                      {errors.country && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.country.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-medium">Post Code</label>
                      <Input
                        {...register("postCode")}
                        className="mt-1 text-xs"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <p className="text-sm font-semibold mb-2">User Role</p>
                  <div className="grid grid-cols-2 gap-6 items-start">
                    <div>
                      <label className="text-xs font-medium">User Role *</label>
                      <select
                        {...register("role", {
                          required: "Role is required",
                        })}
                        className="mt-1 h-9 w-full rounded-md border border-input bg-transparent px-3 text-xs font-medium"
                      >
                        {roleOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <label className="flex items-center gap-2 text-xs mt-6">
                      <input
                        type="checkbox"
                        checked={!!fields?.ssoEnabled}
                        onChange={(e) =>
                          setValue("ssoEnabled", e.target.checked)
                        }
                      />
                      Enable Microsoft SSO for this member
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 border-t px-6 py-4">
                <button
                  type="button"
                  className="rounded-md border border-gray-200 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100"
                  onClick={() => setIsInviteModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center justify-center rounded-md py-2 px-4 gap-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold text-xs"
                  disabled={isInviting}
                >
                  {isInviting ? (
                    <LoadingAnimation className="w-4 h-4" color="white" />
                  ) : null}
                  Invite Members
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {selectedApprovalInvite ? (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white w-full max-w-2xl rounded-lg border border-gray-200 shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <p className="text-sm font-semibold">Recruiter approval request</p>
                <p className="text-xs text-gray-500">
                  Review the inviter details before approving or rejecting.
                </p>
              </div>
              <button
                type="button"
                className="p-1 text-gray-500 hover:text-gray-700"
                onClick={handleCloseApprovalModal}
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="px-6 py-5 grid grid-cols-2 gap-4 text-xs">
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-gray-500">Name</span>
                <span className="text-gray-900">
                  {selectedApprovalInvite.firstName} {selectedApprovalInvite.lastName}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-gray-500">Email</span>
                <span className="text-gray-900">{selectedApprovalInvite.email}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-gray-500">Company</span>
                <span className="text-gray-900">{selectedApprovalInvite.company || "-"}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-gray-500">Role</span>
                <span className="text-gray-900">
                  {roleOptions.find((opt) => opt.value === selectedApprovalInvite.role)?.label || "-"}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-gray-500">Registered</span>
                <span className="text-gray-900">
                  {selectedApprovalInvite.userRegisterAt
                    ? dayjs(selectedApprovalInvite.userRegisterAt).format("YYYY-MM-DD")
                    : "-"}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-gray-500">Approval status</span>
                <span className="text-gray-900">
                  {selectedApprovalInvite.recruiterApprovalStatus ===
                  RecruiterApprovalStatus.REQUESTED
                    ? "Pending approval"
                    : selectedApprovalInvite.recruiterApprovalStatus ===
                        RecruiterApprovalStatus.ACCEPTED
                    ? "Approved"
                    : selectedApprovalInvite.recruiterApprovalStatus ===
                      RecruiterApprovalStatus.REJECTED
                    ? "Rejected"
                    : "Invitation pending"}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t px-6 py-4">
              <button
                type="button"
                className="rounded-md border border-gray-200 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100"
                onClick={handleCloseApprovalModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md border border-[#d72417] px-4 py-2 text-xs font-medium text-[#d72417] hover:bg-[#fbd9ce]"
                onClick={() => handleApprovalAction(2)}
                disabled={approvalActionLoading !== null || !selectedApprovalInvite?.agencyId}
              >
                {approvalActionLoading === "reject" ? "Rejecting..." : "Reject"}
              </button>
              <button
                type="button"
                className="flex items-center justify-center rounded-md py-2 px-4 gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold text-xs"
                onClick={() => handleApprovalAction(1)}
                disabled={approvalActionLoading !== null || !selectedApprovalInvite?.agencyId}
              >
                {approvalActionLoading === "approve" ? (
                  <LoadingAnimation className="w-4 h-4" color="white" />
                ) : null}
                Approve
              </button>
            </div>
            {!selectedApprovalInvite?.agencyId ? (
              <div className="px-6 pb-4 text-xs text-amber-700">
                This invitation has not yet been linked to a recruiter user id,
                so approval cannot be submitted yet.
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </React.Fragment>
  );
};

export default AccountMembers;
